/* eslint-disable */
import React from 'react';
import { useState, useEffect, useMemo } from "react";
import { orderBy } from "lodash";
import { yearToCropYearShort, OLAPTransformerResult, yearToCropYearFull } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, tooltipPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { TableProps } from "../../../components/Table";
import { CustomTooltip } from "../components/Tooltip";
import { SlideContext } from "../Context";
import { useHookSubscribe } from "../../../shared/hooks/hookSubscribe";

const CATEGORIES_AXIS = "commodities.default";
const SERIES_AXIS = "countries.default";
const mapperColors = new Map<string, [string | null, string | null]>();


export function useChart(data: OLAPTransformerResult | null, context: SlideContext): TableProps | undefined {
  const countriesCodes = useHookSubscribe(null, context.countriesCodes$);
  const commoditiesNames = useHookSubscribe(null, context.commoditiesNames$);
  const countriesNames = useHookSubscribe(null, context.countriesNames$);
  const countriesRegionNames = useHookSubscribe(null, context.countriesRegionNames$);

  const orderedCommoditiesIs = commoditiesNames && Array.from(commoditiesNames?.keys());
  const orderedCountriesIs = countriesNames && Array.from(countriesNames?.keys());

  const [state, setState] = useState<TableProps | undefined>();
  const colKey = `${CATEGORIES_AXIS}.name`;
  const formateCol = (colName: string) => colName.replace(',', '').replace('.', '');
  const categories = useMemo(
    () =>
      orderBy(data?.columns, x => orderedCommoditiesIs?.indexOf(Number(x.selection[CATEGORIES_AXIS]))).map((x) => {
        const name = x.headers.find((h) => h.id === colKey)?.name![9];
        return [name, name] as [string, string];
      }) || [],
    [data, orderedCommoditiesIs],
  );

  useEffect(() => {
    if (data) {
      const regions = orderBy(data?.rows.map((x) => {
        const regionName = (countriesRegionNames?.get(Number(x.selection[SERIES_AXIS])) as any);
        return regionName ? regionName[9] : '';
      }));
      const groups = new Set(regions);
      const rows = orderBy(data?.rows, x => orderedCountriesIs?.indexOf(Number(x.selection[SERIES_AXIS]))).map((x) => {
        const regionName = (countriesRegionNames?.get(Number(x.selection[SERIES_AXIS])) as any);
        let region = regionName ? regionName[9] : '';
        if (groups.has(region)) {
          groups.delete(region);
        } else {
          region = "";
        }
        const records = {
          _regionId: Number(x.selection[SERIES_AXIS]),
          _region: region,
          _code: (countriesCodes?.get(Number(x.selection[SERIES_AXIS])) as any),
          Region: region,
          Country: x.attributes[`${SERIES_AXIS}.name`] || "",
        };
        for (const point of x.points) {
          const col = point.attributes ? point.attributes[colKey][9] : "";
          const hasValue = Boolean(point['change']);
          const value = (point['change'] || 0);
          const displayValue = hasValue ? `${Highcharts.numberFormat(value, 1)}%` : "";
          records[formateCol(col)] = displayValue;
          let color: [string | null, string | null] = [null, null];
          if (value < -1) {
            color = [null, "#ff8a65"];
          }
          if (value < -5) {
            color = ["white", "#ef5350"];
          }
          if (value > 1) {
            color = [null, "#a5d6a7"];
          }
          if (value > 5) {
            color = ["white", "#66bb6a"];
          }
          mapperColors.set(displayValue, color);
        }
        return records;
      }); 
      const result: TableProps = {
        columns: ["Region", "Country"]
          .map((x) => [x, x])
          .concat(categories)
          .map(([field, headerName]) => {
            const isExport = Boolean(window['exportMode']);
            const isRegionCol = field === "Region";
            const isCountryCol = field === "Country";
            let width: number | undefined = undefined;
            if (isExport) { 
              if (isRegionCol || isCountryCol) {
                width = isRegionCol ? 116 : 92;
              } else {
                width = 92;
              }
            }

            return ({
              headerName,
              width: width,
              field: formateCol(field),
              resizable: true,
              cellRenderer:
                field === "Country"
                  ? function (params) {
                    let code = params.data["_code"];
                    const flag = `${code}`.toLowerCase();
                    const onlyFlag = isExport;
                    const countryName = onlyFlag ? '' : params.data["Country"];
                    return `<i style="margin-right: ${onlyFlag ? 0 : 8}px; box-shadow: 1px 1px 3px black" class="flag-icon flag-icon-${flag}"></i>${countryName}`;
                  }
                  : undefined,
              cellStyle: function (params) {
                if (mapperColors.has(params.value)) {
                  const [color, backgroundColor] = mapperColors.get(params.value)!;
                  return { color, backgroundColor, textAlign: 'center' };
                }

                if (params.data["Region"] && params.column.getColId() === "Region") {
                  return { backgroundColor: "white", border: "1px solid #f8f9f9", borderLeftWidth: 0 };
                }
                return undefined;
              },
              rowSpan: function (params) {
                if (params.data["Region"] && params.column.getColId() === "Region") {
                  return regions.filter((x) => x === params.data["Region"]).length;
                }
                return 1;
              },
              tooltipValueGetter: function (params) {
                return `${params.data['Country']} - ${params.value}`;
              },
              tooltipComponent: "customTooltip",
              tooltipShowDelay: 0,
            });
          }),
        rows: rows,
        options: {
          suppressRowTransform: true,
          frameworkComponents: {
            customTooltip: CustomTooltip,
          },
        },
      };

      setState(result);
    }
  }, [data, countriesNames]);
  return state;
}
